import React, { useState, useMemo } from "react";
import { accountData } from "./data";

const OrgChart = () => {
  // Default to 100 (Vanguard)
  const [selectedId, setSelectedId] = useState(100);

  const selectedAccount =
    accountData.find((a) => a.id === Number(selectedId)) || accountData[0];

  // --- SMART PARSER ---
  // This converts the comma-separated "stakeholders" string from data.js
  // into a visual tree structure automatically.
  const orgStructure = useMemo(() => {
    const rawString = selectedAccount.stakeholders || "";
    // Split by comma, trim whitespace
    const roles = rawString
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s);

    if (roles.length === 0) {
      return {
        root: { name: "Pending", title: "TBD", status: "Neutral" },
        branches: [],
      };
    }

    // First person is the "Root" (Executive Sponsor)
    const rootRole = roles[0];

    // The rest are branches. We chunk them to make the tree look balanced.
    const remainingRoles = roles.slice(1);
    const branches = remainingRoles.map((role) => ({
      name: role, // In a real app, you'd split Name vs Title. Here we just use the string.
      title: "Stakeholder",
      status: "Neutral",
      children: [], // No deep nesting parsed yet, keeping it flat for 1:Few view
    }));

    return {
      root: { name: rootRole, title: "Executive Sponsor", status: "Champion" },
      branches: branches,
    };
  }, [selectedAccount]);

  // Helper component for a node card
  const NodeCard = ({ node }) => (
    <div className={`org-node ${node.status === "Champion" ? "hero" : ""}`}>
      <div className="avatar">{node.name.charAt(0).toUpperCase()}</div>
      <div className="node-info">
        <div className="node-title">{node.name}</div>
        <div className="node-sub">{node.title}</div>
        {/* Only show tag if it's not neutral to save space */}
        {node.status !== "Neutral" && (
          <div className={`tag ${node.status.toLowerCase()}`}>
            {node.status}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "24px",
        }}
      >
        <div>
          <h2 style={{ fontSize: "20px", fontWeight: 700 }}>
            Buying Committee
          </h2>
          <p style={{ margin: 0 }}>
            Visualizing key stakeholders for {selectedAccount.name}
          </p>
        </div>

        <select
          className="input"
          style={{ width: "240px" }}
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
        >
          {accountData.map((acc) => (
            <option key={acc.id} value={acc.id}>
              {acc.name}
            </option>
          ))}
        </select>
      </div>

      <div
        className="orgsketch"
        style={{ overflowX: "auto", paddingBottom: "20px" }}
      >
        <div className="tree-container">
          {/* Level 1: Root */}
          <div className="tree-level">
            <NodeCard node={orgStructure.root} />
          </div>

          {/* Connector Line (Only if there are branches) */}
          {orgStructure.branches.length > 0 && (
            <div className="tree-lines">
              <div className="line-vert"></div>
            </div>
          )}

          {/* Level 2: Branches */}
          {orgStructure.branches.length > 0 && (
            <div className="tree-level-row">
              {orgStructure.branches.map((branch, i) => (
                <div key={i} className="branch-col">
                  <div className="tree-lines-top">
                    <div className="line-horz"></div>
                    <div className="line-vert-branch"></div>
                  </div>

                  <NodeCard node={branch} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="card analysis" style={{ marginTop: "32px" }}>
        <div className="card-header">
          <h3>Committee Analysis</h3>
        </div>
        <div className="subhead">Engagement Notes</div>
        <ul className="bullets">
          <li>
            <strong>{orgStructure.root.name}</strong> is identified as the
            primary executive entry point.
          </li>
          {selectedAccount.blockers && (
            <li>
              <strong>Current Blocker:</strong> {selectedAccount.blockers}
            </li>
          )}
          <li>
            Strategy: {selectedAccount.strategy || "Develop engagement plan."}
          </li>
        </ul>
      </div>
    </div>
  );
};

export default OrgChart;
